/**
 * PairingView — headless pairing screen.
 *
 * Headless: no `browser.*`, no `chrome.*`, no IPC, no storage hooks.
 * The component is purely presentational — every state value (the
 * 6-digit code, the countdown, the error message) and every
 * transition (retry click) arrives via props. Hosts wire their own
 * pairing state machines: the extension's `START_PAIRING` IPC +
 * `pairingJustConfirmed` storage-watch, or the web's inline keypair-
 * gen + SSE/poll race in `services/pairing.ts`. Mirrors the
 * conventions established by the other headless feature wrappers in
 * this package (`SettingsPanel`, `HeaderBar`, `AppShell`).
 *
 * Five terminal states, driven externally:
 *
 *   - `loading`   — request is in flight; status line announces
 *                    "Requesting pairing code…".
 *   - `showCode`  — 6-box code grid filled with `code`, countdown
 *                    rendered from `secondsLeft` with three-tier
 *                    urgency tone (neutral / secondary @≤60s /
 *                    negative @≤30s).
 *   - `confirmed` — heading swaps to `pairedSuccessfully`, body
 *                    swaps to a centered Check icon + sr-only
 *                    success line.
 *   - `expired`   — empty code grid + "Code expired" status + retry
 *                    button.
 *   - `error`     — empty code grid + `errorMessage` status (or the
 *                    `failedToStartPairing` fallback if empty) +
 *                    retry button.
 *
 * Accessibility:
 *   - The 6-box grid carries `role="status"` (implicit polite live
 *     region). The visible boxes are `aria-hidden` because they're
 *     decorative; an sr-only sibling span carries the announcement
 *     text. Every state transition produces a new live-region text
 *     so AT users hear the change.
 *   - Two-phase publish of the live-region text (empty on mount,
 *     real text via effect) so screen readers see a content CHANGE
 *     rather than a pre-populated string. role="status" only
 *     announces changes.
 *   - In `showCode`, digits are joined with spaces in the live
 *     region (e.g. "4 7 2 8 1 3") so AT announces them as a
 *     positional sequence rather than a 6-figure number.
 *   - The countdown timer is NOT a live region — `aria-live="polite"`
 *     there would batter AT users with a per-second
 *     "2:59… 2:58… 2:57…" stream. The visible tone shift is the
 *     sighted-user cue; the parent grid's role="status" handles
 *     announce-on-change.
 */

import { Check, Lock01 } from "@untitledui/icons";
import { type ReactNode, useEffect, useState } from "react";
import { Button } from "../Button";
import { Logo } from "../Logo";
import { QrCode } from "../QrCode";
import styles from "./PairingView.module.css";

const CODE_LENGTH = 6;

/**
 * Five terminal states the host's pairing FSM produces. Loose union
 * (rather than discriminated record) so the component takes the
 * minimum prop surface — `code` / `secondsLeft` / `errorMessage` are
 * each optional and only consumed in the state where they apply.
 */
export type PairingViewState = "loading" | "showCode" | "confirmed" | "expired" | "error";

/**
 * i18n bag for every visible/announced string.
 */
export interface PairingViewLabels {
  /** Heading shown in the loading / showCode states (and the
   *  expired / error states UNLESS `pairingFailed` is provided). */
  enterCodeInstruction: string;
  /** Heading + sr-only success line in the confirmed state. */
  pairedSuccessfully: string;
  /**
   * Optional heading override for the `expired` and `error` states.
   * When omitted, the heading falls back to `enterCodeInstruction`
   * — which reads as a contradictory directive next to a red error
   * panel (an instruction to "enter this code on your phone" while
   * the body says the request failed). Hosts that want to surface
   * the contextual "Pairing failed" / "Code expired" framing should
   * pass this; hosts that want the legacy single-heading behavior
   * (extension's pre-Phase-E shape) leave it undefined.
   */
  pairingFailed?: string;
  /** Status line + sr-only announce when state === "loading". */
  requestingPairingCode: string;
  /** Status line + sr-only announce when state === "expired". */
  codeExpired: string;
  /** Try Again button label (rendered in the expired / error states). */
  tryAgain: string;
  /** Fallback announce text when state === "error" and `errorMessage` is empty. */
  failedToStartPairing: string;

  // ─── Install + education section (P2/P3/P7/P8 of the pairing-screen
  // education spec) ──────────────────────────────────────────────────
  //
  // Every label below gates its own element EXCEPT `installHeading`,
  // which is part of the section's master gate alongside `installUrl`
  // (a prop, not a label — see PairingViewProps): without a heading
  // there's no framing for the rest of the card, so the whole section
  // renders nothing rather than an orphaned, context-free URL.
  // Otherwise this package ships no English fallback copy, so a host
  // that omits one of the remaining labels still renders a coherent
  // (smaller) block rather than falling back to untranslated text.
  // The section only ever renders in the `loading` / `showCode`
  // states (P8).

  /** Heading for the install block, e.g. "Don't have the app yet?" —
   *  REQUIRED alongside `installUrl` for the section to render at all
   *  (see the block comment above); not independently omittable like
   *  the labels below it. */
  installHeading?: string;
  /** Copy beside the QR framing it as a camera target, never a pairing
   *  target (P3) — e.g. "Scan with your phone's camera". Required
   *  together with `installQrLabel`: the QR and this hint render as one
   *  atomic unit, since an unlabeled scan target defeats P3's anti-
   *  footgun disambiguation. Omitting either omits the QR. */
  installScanHint?: string;
  /** Accessible name for the install QR image. Required together with
   *  `installScanHint` (see above) — omitting either omits the QR
   *  entirely rather than rendering an unlabeled or undisambiguated
   *  image. */
  installQrLabel?: string;
  /** Heading introducing the capability lines, e.g. "Once paired you can:". */
  capabilitiesHeading?: string;
  /** The capability lines themselves (exactly three per the design, but
   *  the component renders however many are supplied). */
  capabilities?: string[];
  /** Trust/privacy note closing the section, e.g. "End-to-end
   *  encrypted — the server never sees your content." */
  encryptionNote?: string;
}

export interface PairingViewProps {
  /** Pairing FSM state (host-owned). */
  state: PairingViewState;
  /** 6-digit code — required when `state === "showCode"`, ignored otherwise. */
  code?: string;
  /** Seconds remaining on the pairing window — required when
   *  `state === "showCode"`, ignored otherwise. Drives the M:SS
   *  display and the three-tier urgency tone. */
  secondsLeft?: number;
  /** Server / network error message — used as the status line + the
   *  sr-only announce when `state === "error"`. Falls back to
   *  `labels.failedToStartPairing` when empty. */
  errorMessage?: string;
  /** Click handler for the Try Again button. Rendered only when
   *  `state === "expired"` OR `state === "error"`. When omitted in
   *  those states the retry row is suppressed entirely — a no-op
   *  interactive button is worse than no button. */
  onRetry?: () => void;
  /** i18n bag for every visible string. */
  labels: PairingViewLabels;
  /** Optional slot rendered beneath the status/countdown line (and the retry
   *  row, when present) in every non-confirmed state — the host page's spot
   *  for pairing-adjacent controls that must stay above the fold (e.g. the
   *  web app's assistant-intent toggle). Hidden in `confirmed` because that
   *  branch renders its own layout. */
  belowStatus?: ReactNode;

  /**
   * Canonical store URL encoded into the install QR (P4 of the pairing-
   * screen education spec — always the Play listing, e.g.
   * `https://play.google.com/store/apps/details?id=dev.reflecto.app`).
   * A prop, not a label: it is not translated copy and must not drift
   * per locale. Also HALF of the section's master gate — the section
   * renders only when this AND `labels.installHeading` are both
   * present (see that label's doc comment); omitting either degrades
   * the screen to today's layout with no orphaned URL left behind.
   *
   * SECURITY: this is ALWAYS the public install URL, never the 6-digit
   * pairing `code`. The pairing code is a short-lived secret and must
   * never be encoded into a QR or logged — see `QrCode`'s own security
   * note. `PairingView` enforces this by construction: `code` is never
   * read anywhere near the QR's `value`.
   */
  installUrl?: string;
  /**
   * Short human-typable URL rendered as selectable text beside the QR
   * (P5) — e.g. "reflecto.dev/get". Doubles as the accessibility
   * fallback for the QR image. A prop, not a label, for the same
   * no-locale-drift reason as `installUrl`. Independent of
   * `labels.installScanHint` — omitting one does not affect the other.
   */
  installTypedUrl?: string;
}

/** Format seconds as M:SS for the countdown timer. */
function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function PairingView({
  state,
  code = "",
  secondsLeft = 0,
  errorMessage = "",
  onRetry,
  labels,
  belowStatus,
  installUrl,
  installTypedUrl,
}: PairingViewProps) {
  // Boxes are always 6 cells; fill from `code` when present, leave
  // empty otherwise. Layout stays stable across state transitions
  // (no width/height shift between loading → showCode → expired,
  // which would be jarring).
  const showFilledCode = state === "showCode";

  // Text for the codeGrid's live region. This is read by screen
  // readers both when the user navigates to the region AND when the
  // string changes (a live-region transition). The visible boxes are
  // `aria-hidden` for purely decorative layout, so this string is the
  // ONLY accessible text content inside the `role="status"` container
  // — without it, AT users hear nothing on state changes and never
  // learn the pairing code.
  //
  // In `showCode`, digits are joined with spaces so screen readers
  // announce "4 7 2 8 1 3" rather than "four hundred seventy-two
  // thousand…" — the code is a positional sequence, not a number.
  //
  // The `confirmed` state takes a separate render branch below and
  // never reaches this code path; intentionally not handled here.
  let computedLiveRegionText = "";
  if (showFilledCode) {
    computedLiveRegionText = `${labels.enterCodeInstruction}: ${code.split("").join(" ")}`;
  } else if (state === "loading") {
    computedLiveRegionText = labels.requestingPairingCode;
  } else if (state === "expired") {
    computedLiveRegionText = labels.codeExpired;
  } else if (state === "error") {
    // Belt-and-suspenders: prefer the host-supplied errorMessage but
    // fall back to the generic-failure string (not the "Pair your
    // phone" heading) so AT users still hear an error signal if the
    // host ever leaves errorMessage empty.
    computedLiveRegionText = errorMessage || labels.failedToStartPairing;
  }

  // Two-phase publish of the announcement text: render once with ""
  // on mount, then update to the real text via effect so screen
  // readers see a content CHANGE (empty → text) rather than a pre-
  // populated string. role="status" live regions only announce
  // changes — content present on mount is silently skipped. This
  // matters for the initial "Requesting pairing code…" announcement;
  // subsequent transitions (loading → showCode etc.) are naturally
  // changes and would announce either way.
  const [liveRegionText, setLiveRegionText] = useState("");
  useEffect(() => {
    setLiveRegionText(computedLiveRegionText);
  }, [computedLiveRegionText]);

  // Heading derivation — same top-down conditional pattern used
  // throughout this component so the three branches read sequentially:
  //   - confirmed  → success heading
  //   - error|expired with `pairingFailed` set → contextual override
  //   - everything else (including error/expired with no override) →
  //     the legacy enterCodeInstruction heading
  let headingText = labels.enterCodeInstruction;
  if (state === "confirmed") {
    headingText = labels.pairedSuccessfully;
  } else if ((state === "error" || state === "expired") && labels.pairingFailed) {
    headingText = labels.pairingFailed;
  }

  // Install + education section (design spec P2/P3/P7/P8). Master gate:
  // `installUrl` AND `labels.installHeading` AND being in `loading` or
  // `showCode` (P8: the `expired`/`error` retry action is the focus in
  // those states and must not compete for attention).
  //
  // `installHeading` is part of the MASTER gate, not an independently-
  // omittable label like the others below it (fix-round finding I-1):
  // P2 calls this "a distinct block... own section, its own heading" —
  // without that heading there is no framing for a lone card or a
  // context-free typed URL to sit under, which is exactly the residue
  // a locale catalog missing every install key would otherwise leave
  // behind (extension `_locales` has no compile-time backstop; a
  // missing key resolves to `""`, which is falsy). Requiring the
  // heading means a locale that ships NONE of these keys renders
  // NOTHING here — the correct degrade — while a locale that ships at
  // least the heading still gets a coherent, if smaller, block.
  //
  // Every OTHER sub-element below gates on its own label, so a host
  // that supplies `installUrl` + `installHeading` but omits e.g.
  // `labels.encryptionNote` still renders a coherent (smaller) block
  // rather than substituting untranslated English.
  //
  // SECURITY: `installUrl` — never `code` — is the only thing ever
  // passed as `QrCode`'s `value`. The 6-digit pairing code is a
  // short-lived secret and must never be encoded into a QR (see
  // `QrCode`'s own security note); this component enforces that by
  // construction, since `code` is never referenced anywhere in this
  // block.
  //
  // P3's anti-footgun copy ("scan with your phone's CAMERA, not the
  // app") is carried entirely by `installScanHint` — an image with no
  // accompanying disambiguation text is exactly the ambiguity P3 exists
  // to prevent (fix-round finding: treat the hint as required-with-the-
  // QR). So the QR and its scan hint render as a single atomic unit:
  // both present or neither renders, never one without the other.
  const qrReady =
    labels.installQrLabel && labels.installScanHint
      ? { label: labels.installQrLabel, hint: labels.installScanHint }
      : null;

  let installSection: ReactNode = null;
  if (installUrl && labels.installHeading && (state === "loading" || state === "showCode")) {
    installSection = (
      // `.installSection` IS the card (P9a) — `--bg-secondary` /
      // `--radius-8` / `--spacing-16` padding, the same surface
      // vocabulary `Notification` and `ClipCard` use elsewhere in this
      // package. The card's own bounded, elevated surface is now the
      // "visually separated" cue P2 asked for, so the hairline divider
      // this block used to render above itself is gone — a card
      // floating on the page background already reads as a distinct
      // block; stacking a divider on top of it doubled up the framing
      // the founder dogfood flagged as one source of "ugly".
      <div className={styles.installSection}>
        {/* P9b: real type hierarchy. `.title` above (the code
         *  instruction) is the dominant element on the whole screen and
         *  is untouched here. This heading is "a clear step below" it —
         *  same 14px body size as before, but demoted from semibold/
         *  primary to medium/secondary so it can't compete with `.title`
         *  for attention. */}
        <h3 className={styles.installHeading}>{labels.installHeading}</h3>

        <div className={styles.installRow}>
          {/* `QrCode.label` is a required prop (a QR is an image and
           *  needs a text equivalent), and `installScanHint` is
           *  required-with-the-QR (see `qrReady` above) — omitting
           *  either omits the QR entirely rather than rendering an
           *  unlabeled or undisambiguated image. P9d: the well div is a
           *  container OUTSIDE the QR's own opaque white field — it
           *  adds padding/radius/contrast around the image, never on
           *  top of it, so the quiet zone and light-on-dark contrast a
           *  real camera scan depends on stay untouched (see QrCode's
           *  own security/scan-reliability notes). */}
          {qrReady && (
            <div className={styles.installQrWell}>
              <QrCode
                value={installUrl}
                label={qrReady.label}
                size={96}
                className={styles.installQr}
              />
            </div>
          )}
          <div className={styles.installTextCol}>
            {qrReady && <p className={styles.installScanHint}>{qrReady.hint}</p>}
            {/* `installTypedUrl` is a prop (not translated copy — see
             *  its doc comment) and gates independently of the QR
             *  block: it is both the typing affordance AND the
             *  accessibility fallback for the QR image (P5), so it
             *  must exist as real selectable text, not only inside the
             *  QR's `alt` — and it still has a use (typing instead of
             *  scanning) even when the QR itself is suppressed. P9e:
             *  styled to read as a URL (brand-toned, regular weight)
             *  rather than a bold heading that outweighs the scan hint
             *  above it. */}
            {installTypedUrl && <p className={styles.installTypedUrl}>{installTypedUrl}</p>}
          </div>
        </div>

        {/* P9b/P9c: this is a LABEL introducing the list below, not a
         *  third heading — quiet uppercase/tracked treatment (same
         *  demotion `SettingsPanel`'s `.sectionHeader` uses for its
         *  group labels), left-aligned to read as one block with the
         *  left-aligned list it introduces. Deliberately a `<p>`, not
         *  an `<h4>`: it no longer participates in the document's
         *  heading outline once it stops behaving like a heading. */}
        {labels.capabilitiesHeading && (
          <p className={styles.capabilitiesHeading}>{labels.capabilitiesHeading}</p>
        )}
        {labels.capabilities && labels.capabilities.length > 0 && (
          <ul className={styles.capabilitiesList}>
            {labels.capabilities.map((line, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static, host-supplied, order-stable copy list
              <li key={i}>{line}</li>
            ))}
          </ul>
        )}

        {labels.encryptionNote && (
          <p className={styles.encryptionNote}>
            <Lock01 size={12} aria-hidden="true" />
            {labels.encryptionNote}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* First-run brand moment. AppShell omits HeaderBar chrome until
       *  the user is paired, so the Logo anchors this screen. `lg`
       *  (32px) matches Figma 1148:562 and gives the wordmark enough
       *  presence to own the top third of the screen without tipping
       *  into `xl` (48px) dominance. `decorative` because the h2
       *  below already announces the directive. */}
      <div className={styles.brandMark}>
        <Logo size="lg" decorative />
      </div>

      {/* Single-line heading. The 6-box grid below makes "this is a
       *  pairing code" implicit; the directive alone carries both the
       *  identity and the action.
       *
       *  In the `confirmed` state the body swaps to a success
       *  checkmark (no code shown), so the heading text swaps too —
       *  otherwise a screen-reader user navigating by headings would
       *  hear "Enter this code…" with no code beneath it. The h2
       *  element stays in the tree (no reflow); only its text
       *  updates. The error / expired states optionally swap to
       *  `labels.pairingFailed` so the heading doesn't read as a
       *  contradictory "enter this code" instruction next to a red
       *  error panel; falls back to `enterCodeInstruction` when the
       *  host omits the override (extension's pre-Phase-E shape).
       *  Heading derivation lives above. */}
      <h2 className={styles.title}>{headingText}</h2>

      {/* State-specific body */}
      {state === "confirmed" ? (
        <div className={styles.centered}>
          <div className={styles.checkmark}>
            <Check size={48} aria-hidden="true" />
          </div>
          {/* aria-hidden because the h2 above already announces
           *  "Paired successfully!" in the confirmed state. Without
           *  this, screen readers would announce the same text twice
           *  (once from the heading, once from this paragraph). The
           *  `<p>` exists purely for the green success tone. */}
          <p className={styles.success} aria-hidden="true">
            {labels.pairedSuccessfully}
          </p>
        </div>
      ) : (
        <div className={styles.codeArea}>
          {/* Live region for code-state announcements. role="status"
           *  gives an implicit polite live region; the visually-hidden
           *  <span> below carries the text that screen readers
           *  announce on transition. The individual code boxes are
           *  decorative (aria-hidden) — without the hidden span the
           *  region's accessible text would be "" and AT users would
           *  miss every state change AND never hear the code digits. */}
          <div className={styles.codeGrid} role="status">
            <span className={styles.srOnly}>{liveRegionText}</span>
            {Array.from({ length: CODE_LENGTH }).map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: digits are positional; index IS the identity
                key={i}
                className={styles.codeBox}
                aria-hidden="true"
              >
                {showFilledCode && code[i] ? (
                  <span className={styles.codeDigit}>{code[i]}</span>
                ) : null}
              </div>
            ))}
          </div>

          {/* Status line beneath the grid — swaps per state. NOT a
           *  live region itself: the parent codeGrid already carries
           *  `role="status"` and its sr-only sibling provides the
           *  announce-on-change text. Nesting a second live region
           *  here would cause double-announcements in some screen
           *  readers. */}
          {state === "loading" && <p className={styles.status}>{labels.requestingPairingCode}</p>}
          {state === "showCode" && (
            <p
              // Tone escalates as the code's life runs out: tertiary
              // neutral → secondary warn (≤60s) → negative urgent (≤30s).
              // Encodes urgency without adding copy.
              //
              // NOT a live region: `aria-live="polite"` here would
              // fire an announcement every second for the full
              // countdown, battering screen-reader users with
              // "2:59… 2:58… 2:57…" interruptions. The visible tone
              // shift is a sighted-user cue.
              className={[
                styles.timer,
                secondsLeft <= 30
                  ? styles.timerUrgent
                  : secondsLeft <= 60
                    ? styles.timerWarning
                    : null,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {formatTime(secondsLeft)}
            </p>
          )}
          {state === "expired" && <p className={styles.errorText}>{labels.codeExpired}</p>}
          {state === "error" && (
            <p className={styles.errorText}>{errorMessage || labels.failedToStartPairing}</p>
          )}

          {(state === "expired" || state === "error") && onRetry && (
            // Matches the Figma 1148:562 primary CTA (cyan→green
            // gradient, full-width, 40px tall) via the Button
            // primitive's `primary` + `fullWidth` props — no
            // hand-rolled retry styles. The retry row stretches to
            // the container's full width so the button can fill it.
            <div className={styles.retryRow}>
              <Button variant="primary" size="m" fullWidth onClick={onRetry}>
                {labels.tryAgain}
              </Button>
            </div>
          )}

          {belowStatus}

          {installSection}
        </div>
      )}
    </div>
  );
}
